import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE_NAME = "Pferdeosteopathie Laskowski";
const HOME_TITLE = `${SITE_NAME} – Hamburg und Umgebung`;
export const SITE_URL = "https://pferdeosteopathie-laskowski.de";
export const LOCAL_BUSINESS_ID = `${SITE_URL}/#localbusiness`;

export type BreadcrumbItem = { name: string; path: string };

type JsonLd = Record<string, unknown>;

export default function SEO({
  title,
  description,
  noindex = false,
  canonical,
  breadcrumbs,
  jsonLd,
}: {
  title: string;
  description: string;
  noindex?: boolean;
  /**
   * Optional override. By default the canonical is derived from the current
   * route, with trailing slashes stripped (except for "/") and query strings
   * dropped. Pass this to point a page at a different canonical URL.
   */
  canonical?: string;
  /**
   * Breadcrumb trail rendered as schema.org BreadcrumbList JSON-LD. Order
   * matters; the last item should be the current page.
   */
  breadcrumbs?: BreadcrumbItem[];
  /**
   * Additional schema.org JSON-LD payload(s). Each entry must already include
   * its own `@context` and `@type`.
   */
  jsonLd?: JsonLd | JsonLd[];
}) {
  const { pathname } = useLocation();
  const fullTitle = title === SITE_NAME ? HOME_TITLE : `${title} | ${SITE_NAME}`;

  // Match the sitemap form: "/" stays "/", every other path is stripped of
  // trailing slashes. Query strings are excluded because we build from
  // `pathname`, not `pathname + search` — that collapses variants like
  // /kontakt?betreff=Terminanfrage+Akupunktur back to /kontakt.
  const normalizedPath =
    pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
  const canonicalUrl = canonical ?? `${SITE_URL}${normalizedPath}`;

  const schemas: JsonLd[] = [];
  if (breadcrumbs && breadcrumbs.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.name,
        item: `${SITE_URL}${b.path}`,
      })),
    });
  }
  if (jsonLd) {
    if (Array.isArray(jsonLd)) schemas.push(...jsonLd);
    else schemas.push(jsonLd);
  }

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {!noindex && <link rel="canonical" href={canonicalUrl} />}
      {noindex && <meta name="robots" content="noindex" />}
      {schemas.map((schema, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
